import {
  Tag,
  Text,
  TypeList,
  TypeListItem,
} from '@spotify-internal/encore-web';
import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  RuleFragment,
  VariantFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';

import styles from './VariantItem.module.css';

export const buildVariantUsage = (
  rules: ReadonlyArray<RuleFragment>,
): Record<string, number> => {
  const counts: Record<string, number> = {};
  rules.forEach(rule => {
    rule.assignmentSpec?.assignments?.forEach(assignment => {
      const variantName = getTypeOrNull(assignment.variant?.variant)?.name;
      if (variantName) {
        counts[variantName] = (counts[variantName] ?? 0) + 1;
      }
    });
  });
  return counts;
};

interface VariantItemProps {
  variant: VariantFragment;
  usageCount: number;
}

const VariantItem = ({ variant, usageCount }: VariantItemProps) => {
  const inUse = usageCount > 0;
  const tagLabel = inUse ? 'In use' : 'Unused';
  const name = extractLastNameComponent(variant.name) ?? variant.name;

  return (
    <TypeListItem condensed variant="bodySmall">
      <span className={styles.header}>
        <Text variant="bodySmall">{name}</Text>
        <Tag colorSet={inUse ? 'positiveSubdued' : undefined}>{tagLabel}</Tag>
      </span>
      {variant.description && (
        <Text
          className={styles.details}
          variant="bodySmall"
          semanticColor="textSubdued"
        >
          {variant.description}
        </Text>
      )}
    </TypeListItem>
  );
};

interface VariantListProps {
  variants: ReadonlyArray<VariantFragment>;
  variantUsage: Record<string, number>;
}

export const VariantList = ({ variants, variantUsage }: VariantListProps) => (
  <TypeList className={styles.list} condensedAll>
    {variants.map(variant => (
      <VariantItem
        key={variant.name}
        variant={variant}
        usageCount={variantUsage[variant.name] ?? 0}
      />
    ))}
  </TypeList>
);
